import { useEffect, useState } from 'react'

const sizeClasses = {
  /** Matches PopularDestinationCountryCard grid row */
  compact: 'h-10 w-10 min-h-10 min-w-10 text-[1.75rem]',
  sm: 'h-9 w-9 min-h-9 min-w-9 text-2xl',
  md: 'h-11 w-11 min-h-11 min-w-11 text-3xl',
  lg: 'h-16 w-16 min-h-16 min-w-16 text-5xl md:h-[4.5rem] md:w-[4.5rem] md:min-h-[4.5rem] md:min-w-[4.5rem]',
} as const

export type CountryFlagCircleSize = keyof typeof sizeClasses

export interface CountryFlagCircleProps {
  isoCode?: string | null
  emojiFallback: string
  size?: CountryFlagCircleSize
  alt?: string
  className?: string
  /** Skip flagcdn.com — use emoji only (home grids already have emoji). */
  preferEmoji?: boolean
}

function normalizeIso(iso: string | null | undefined): string | null {
  if (!iso?.trim()) return null
  const upper = iso.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(upper) ? upper : null
}

/**
 * Renders a circular flag (image from ISO-2 when possible, else emoji/text fallback).
 */
export function CountryFlagCircle({
  isoCode,
  emojiFallback,
  size = 'md',
  alt = '',
  className = '',
  preferEmoji = false,
}: CountryFlagCircleProps) {
  const normalizedIso = normalizeIso(isoCode)
  const flagUrl =
    !preferEmoji && normalizedIso !== null
      ? `https://flagcdn.com/w160/${normalizedIso.toLowerCase()}.png`
      : null
  const [showImage, setShowImage] = useState(Boolean(flagUrl))

  useEffect(() => {
    setShowImage(Boolean(flagUrl))
  }, [flagUrl])

  const box = `${sizeClasses[size]} flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-50 ring-1 ring-gray-100`

  return (
    <div className={`${box} ${className}`.trim()} role="img" aria-label={alt || undefined}>
      {showImage && flagUrl ? (
        <img
          src={flagUrl}
          alt={alt}
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setShowImage(false)}
        />
      ) : (
        <span className="flex select-none items-center justify-center leading-none">{emojiFallback}</span>
      )}
    </div>
  )
}
